import React, { useState, useEffect } from 'react';
import { PageHeader, Card, StatusBadge, LoadingSpinner } from '../components/Layout';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Types
interface DealerPortalUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_work?: string;
  phone_mobile?: string;
  phone_home?: string;
  company?: string;
  dealer_id?: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  address_country?: string;
  discount_percent?: number;
  dealer_type?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  employee_id: string;
  email: string;
  phone_work?: string;
  phone_mobile?: string;
  job_title?: string;
  department?: string;
  hire_date?: string;
  hourly_rate?: number;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  notes?: string;
  status: string;
  all_access: boolean;
  // Address (for tax document mailing)
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  // Payroll Classification
  payroll_classification?: string;
  payroll_pay_type?: string;
  payroll_pay_rate?: number;
  payroll_filing_status?: string;
  // Permissions
  workstation_access: boolean;
  workstation_pin?: string;
  assigned_workstation_ids?: string[];
  factory_admin_access: boolean;
  factory_admin_role?: string;
  sales_portal_access: boolean;
  sales_role?: string;
  sales_team?: string;
  territory_ids?: string[];
  selected_states?: string[];  // Specific states within territories for granular control
  reports_to_id?: string;
  reports_to_name?: string;
  commission_rate_override?: number;
  shipping_portal_access: boolean;
  shipping_role?: string;
  maintenance_access: boolean;
  maintenance_role?: string;
  rep_portal_access: boolean;
  health_dashboard_access: boolean;
  accounting_access: boolean;
  can_edit_manufacturing_rules: boolean;
  can_add_inventory: boolean;
  work_cards_only?: boolean;
  lock_production_mode?: boolean;
  discipline_access?: boolean;
  reviews_access?: boolean;
  discipline_subject?: boolean;
  assigned_dealer_count?: number;
  created_at: string;
  updated_at: string;
}

interface Territory {
  id: string;
  code: string;
  name: string;
  states: string[];
}

interface Workstation {
  id: string;
  name: string;
  group_name?: string;
  is_active: boolean;
}

type TabType = 'dealer-portal' | 'employees' | 'rep-firm';

// US States for dropdown
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const COUNTRIES = ['United States', 'Canada', 'Mexico'];

const DEPARTMENTS = ['Production', 'Shipping', 'QC', 'Assembly', 'Cutting', 'Maintenance', 'Office', 'Sales'];

const DEALER_TYPES = [
  { value: 'core_window_treatment', label: 'Core Window Treatment' },
  { value: 'cedia_industry', label: 'CEDIA Industry' },
];

const FACTORY_ADMIN_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'viewer', label: 'Viewer' },
];

const SALES_ROLES = [
  { value: 'vp_of_sales', label: 'VP of Sales' },
  { value: 'regional_director', label: 'Regional Director' },
  { value: 'territory_manager', label: 'Territory Manager' },
  { value: 'sales_rep', label: 'Sales Rep' },
];

const SALES_TEAMS = [
  { value: 'core_window_treatment', label: 'Core Window Treatment' },
  { value: 'cedia_industry', label: 'CEDIA Industry' },
];

const SHIPPING_ROLES = [
  { value: 'shipping_admin', label: 'Shipping Admin' },
  { value: 'shipping_employee', label: 'Shipping Employee' },
];

const MAINTENANCE_ROLES = [
  { value: 'admin', label: 'Maintenance Admin' },
  { value: 'manager', label: 'Maintenance Manager' },
  { value: 'supervisor', label: 'Maintenance Supervisor' },
  { value: 'technician', label: 'Maintenance Technician' },
];

const PAYROLL_CLASSIFICATIONS = [
  { value: 'w2_hourly', label: 'W-2 Hourly' },
  { value: 'w2_salary', label: 'W-2 Salary' },
  { value: '1099_contractor', label: '1099 Contractor' },
  { value: '1099_commission', label: '1099 Commission Only' },
  { value: '1099_mixed', label: '1099 Mixed (Hourly + Commission)' },
];

const PAY_TYPES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'salary', label: 'Salary' },
  { value: 'commission', label: 'Commission Only' },
  { value: 'mixed', label: 'Mixed (Hourly + Commission)' },
];

const FILING_STATUSES = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married Filing Jointly' },
  { value: 'head_of_household', label: 'Head of Household' },
];

// API helper
async function apiRequest<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
}

// Tab Button Component
function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-4 py-2 rounded-lg font-medium transition-colors',
        active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      )}
    >
      {children}
    </button>
  );
}

// Modal Component
function Modal({ isOpen, onClose, title, children, wide = false }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className={clsx(
          "relative bg-white rounded-xl shadow-2xl w-full max-h-[90vh] overflow-y-auto",
          wide ? "max-w-4xl" : "max-w-2xl"
        )}>
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Form Input Component
function FormInput({
  label,
  name,
  type = 'text',
  value,
  onChange,
  required = false,
  placeholder = '',
  disabled = false,
  minLength,
  hint,
}: {
  label: string;
  name: string;
  type?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  minLength?: number;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        onWheel={type === 'number' ? (e) => e.currentTarget.blur() : undefined}
        required={required}
        placeholder={placeholder}
        disabled={disabled}
        minLength={minLength}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

// Form Select Component
function FormSelect({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  placeholder = 'Select...',
  disabled = false,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: string[] | { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => {
          const optValue = typeof opt === 'string' ? opt : opt.value;
          const optLabel = typeof opt === 'string' ? opt : opt.label;
          return <option key={optValue} value={optValue}>{optLabel}</option>;
        })}
      </select>
    </div>
  );
}

// Toggle Switch Component
// Per-employee PTO/vacation allocation editor (admin). Reads/writes the
// existing employee_pto_balances via /time-off/admin/balances. Vacation etc.
// resets yearly and is set manually here ("set what's left this year").
function TimeOffBalancesSection({ employeeId }: { employeeId: string }) {
  // Single visible PTO bucket for now (uses the 'vacation' balance_type).
  // Sick/Personal are commented out until they're turned on later — the
  // backend still carries all three types, so re-enabling is uncommenting.
  const TYPES: Array<{ key: string; label: string }> = [
    { key: 'vacation', label: 'PTO' },
    // { key: 'sick', label: 'Sick' },
    // { key: 'personal', label: 'Personal' },
  ];
  const [balances, setBalances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState('');

  const load = async () => {
    try {
      const data = await apiRequest(`/time-off/admin/balances/${employeeId}`);
      setBalances(Array.isArray(data) ? data : []);
    } catch {
      setBalances([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const byType = (t: string) => balances.find((b) => b.balance_type === t);

  const save = async (t: string) => {
    const raw = edits[t];
    if (raw === undefined || raw === '') return;
    const hours = parseFloat(raw);
    if (!Number.isFinite(hours) || hours < 0) { setMsg('Enter a valid number of hours'); return; }
    setSaving(t); setMsg('');
    try {
      // Set the allocation in HOURS and reset "used" to 0 so available = hours
      // remaining. Backend converts hours → stored days via the standard day.
      await apiRequest(`/time-off/admin/balances/${employeeId}/${t}`, {
        method: 'PATCH',
        body: JSON.stringify({ total_hours: hours, used_hours: 0 }),
      });
      setEdits((e) => ({ ...e, [t]: '' }));
      setMsg(`${t} set to ${hours} hour(s)`);
      await load();
    } catch (err: any) {
      setMsg(err.message || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Time Off Balances</h3>
      <p className="text-sm text-gray-500 mb-4">
        PTO is granted automatically — 40 hours at the 1-year mark, reset to 40 each Jan 1 (no rollover).
        You can override the HOURS remaining here; setting a value resets "used" to 0 so the amount entered is what's available.
      </p>
      {loading ? (
        <p className="text-sm text-gray-400">Loading balances…</p>
      ) : (
        <div className="space-y-3">
          {TYPES.map(({ key, label }) => {
            const b = byType(key);
            return (
              <div key={key} className="flex items-center gap-3">
                <div className="w-24 font-medium text-gray-800">{label}</div>
                <div className="text-sm text-gray-500 w-52">
                  Available: {b ? Number(b.available_hours ?? 0).toFixed(2) : '0.00'} / Total: {b ? Number(b.total_hours ?? 0).toFixed(2) : '0.00'} hour(s)
                </div>
                <input
                  type="number"
                  min={0}
                  step="0.25"
                  placeholder="hours"
                  value={edits[key] ?? ''}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                  className="w-28 px-2 py-1 border border-gray-300 rounded-lg text-sm"
                />
                <span className="text-xs text-gray-400">hrs</span>
                <button
                  type="button"
                  disabled={saving === key || edits[key] === undefined || edits[key] === ''}
                  onClick={() => save(key)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg disabled:bg-gray-300"
                >
                  {saving === key ? 'Saving…' : 'Set'}
                </button>
              </div>
            );
          })}
          {msg && <p className="text-sm text-gray-600">{msg}</p>}
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  highlight = false,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  highlight?: boolean;
}) {
  return (
    <label className={clsx(
      "flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors",
      disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50",
      highlight && checked && "bg-purple-50 border border-purple-200"
    )}>
      <div className="pt-0.5">
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={disabled}
          onClick={() => !disabled && onChange(!checked)}
          className={clsx(
            "relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
            checked ? (highlight ? "bg-purple-600" : "bg-blue-600") : "bg-gray-200"
          )}
        >
          <span
            className={clsx(
              "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
              checked ? "translate-x-5" : "translate-x-0"
            )}
          />
        </button>
      </div>
      <div className="flex-1">
        <span className={clsx("font-medium", highlight ? "text-purple-700" : "text-gray-900")}>{label}</span>
        {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
      </div>
    </label>
  );
}

// Multi-Select Component
function MultiSelect({
  label,
  options,
  selected,
  onChange,
  disabled = false,
}: {
  label: string;
  options: { id: string; name: string; group_name?: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}) {
  const toggle = (id: string) => {
    if (disabled) return;
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className={clsx(
        "border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto",
        disabled && "bg-gray-100"
      )}>
        {options.length === 0 ? (
          <p className="text-gray-500 text-sm">No options available</p>
        ) : (
          options.map((opt) => (
            <label key={opt.id} className={clsx(
              "flex items-center gap-2 p-1 rounded",
              disabled ? "cursor-not-allowed" : "hover:bg-gray-50 cursor-pointer"
            )}>
              <input
                type="checkbox"
                checked={selected.includes(opt.id)}
                onChange={() => toggle(opt.id)}
                disabled={disabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">{opt.name}</span>
              {opt.group_name && <span className="text-xs text-gray-500">({opt.group_name})</span>}
            </label>
          ))
        )}
      </div>
    </div>
  );
}

// Territory & State Select Component with Check All
function TerritoryStateSelect({
  territories,
  selectedTerritories,
  selectedStates,
  onTerritoriesChange,
  onStatesChange,
  disabled = false,
}: {
  territories: Territory[];
  selectedTerritories: string[];
  selectedStates: string[];
  onTerritoriesChange: (selected: string[]) => void;
  onStatesChange: (selected: string[]) => void;
  disabled?: boolean;
}) {
  const [expandedTerritories, setExpandedTerritories] = useState<string[]>([]);

  const allTerritoryIds = territories.map((t) => t.id);
  const allSelected = allTerritoryIds.length > 0 && allTerritoryIds.every((id) => selectedTerritories.includes(id));
  const someSelected = selectedTerritories.length > 0 && !allSelected;

  const handleCheckAll = () => {
    if (disabled) return;
    if (allSelected) {
      onTerritoriesChange([]);
      onStatesChange([]);
    } else {
      onTerritoriesChange(allTerritoryIds);
    }
  };

  const toggleTerritory = (territoryId: string) => {
    if (disabled) return;
    const territory = territories.find((t) => t.id === territoryId);
    if (selectedTerritories.includes(territoryId)) {
      onTerritoriesChange(selectedTerritories.filter((id) => id !== territoryId));
      // Also remove all states from this territory
      if (territory) {
        onStatesChange(selectedStates.filter((s) => !territory.states.includes(s)));
      }
    } else {
      onTerritoriesChange([...selectedTerritories, territoryId]);
    }
  };

  const toggleState = (state: string, territoryId: string) => {
    if (disabled) return;
    // Ensure territory is selected when selecting a state
    if (!selectedTerritories.includes(territoryId)) {
      onTerritoriesChange([...selectedTerritories, territoryId]);
    }
    if (selectedStates.includes(state)) {
      onStatesChange(selectedStates.filter((s) => s !== state));
    } else {
      onStatesChange([...selectedStates, state]);
    }
  };

  const toggleExpand = (territoryId: string) => {
    if (expandedTerritories.includes(territoryId)) {
      setExpandedTerritories(expandedTerritories.filter((id) => id !== territoryId));
    } else {
      setExpandedTerritories([...expandedTerritories, territoryId]);
    }
  };

  const getStatesSelectedCount = (territory: Territory) => {
    return territory.states.filter((s) => selectedStates.includes(s)).length;
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Territories & States</label>
      <div className={clsx(
        "border border-gray-300 rounded-lg p-2 max-h-64 overflow-y-auto",
        disabled && "bg-gray-100"
      )}>
        {/* Check All */}
        <label className={clsx(
          "flex items-center gap-2 p-2 rounded bg-blue-50 mb-2 border border-blue-200",
          disabled ? "cursor-not-allowed" : "hover:bg-blue-100 cursor-pointer"
        )}>
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => { if (el) el.indeterminate = someSelected; }}
            onChange={handleCheckAll}
            disabled={disabled}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-semibold text-blue-700">Check All Territories</span>
          <span className="text-xs text-blue-500 ml-auto">
            {selectedTerritories.length}/{territories.length} selected
          </span>
        </label>

        {territories.length === 0 ? (
          <p className="text-gray-500 text-sm">No territories available</p>
        ) : (
          territories.map((territory) => {
            const isExpanded = expandedTerritories.includes(territory.id);
            const isSelected = selectedTerritories.includes(territory.id);
            const statesCount = getStatesSelectedCount(territory);

            return (
              <div key={territory.id} className="mb-1">
                {/* Territory Header */}
                <div className={clsx(
                  "flex items-center gap-2 p-1.5 rounded",
                  isSelected ? "bg-green-50" : "hover:bg-gray-50"
                )}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleTerritory(territory.id)}
                    disabled={disabled}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => toggleExpand(territory.id)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className={clsx("w-4 h-4 transition-transform", isExpanded && "rotate-90")} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <span className={clsx("text-sm font-medium", isSelected && "text-green-700")}>
                    {territory.code} - {territory.name}
                  </span>
                  {statesCount > 0 && (
                    <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded ml-auto">
                      {statesCount} state{statesCount > 1 ? 's' : ''} limited
                    </span>
                  )}
                </div>

                {/* States (Expanded) */}
                {isExpanded && (
                  <div className="ml-8 pl-2 border-l-2 border-gray-200 mt-1 mb-2">
                    <p className="text-xs text-gray-500 mb-1">
                      {statesCount === 0 ? 'All states included (select specific states to limit)' : `Limited to ${statesCount} state${statesCount > 1 ? 's' : ''}`}
                    </p>
                    {territory.states.map((state) => (
                      <label key={state} className={clsx(
                        "flex items-center gap-2 p-1 rounded text-sm",
                        disabled ? "cursor-not-allowed" : "hover:bg-gray-50 cursor-pointer",
                        selectedStates.includes(state) && "bg-orange-50"
                      )}>
                        <input
                          type="checkbox"
                          checked={selectedStates.includes(state)}
                          onChange={() => toggleState(state, territory.id)}
                          disabled={disabled}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className={clsx(selectedStates.includes(state) && "text-orange-700")}>{state}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Select territories for full coverage. Expand to limit to specific states within a territory.
      </p>
    </div>
  );
}

// Change Password Modal
function ChangePasswordModal({
  isOpen,
  onClose,
  userType,
  userId,
  userName,
}: {
  isOpen: boolean;
  onClose: () => void;
  userType: string;
  userId: string;
  userName: string;
}) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const endpoint = userType === 'employees'
        ? `/employees/${userId}/set-password`
        : `/users/${userType}/${userId}/set-password`;
      await apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({ password, confirm_password: confirmPassword }),
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setPassword('');
        setConfirmPassword('');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Change Password for ${userName}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
        )}
        {success && (
          <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg">Password changed successfully!</div>
        )}

        <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-lg text-sm">
          <strong>Password Requirements:</strong>
          <ul className="list-disc ml-5 mt-1">
            <li>Minimum 8 characters</li>
          </ul>
        </div>

        <FormInput
          label="New Password"
          name="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
        />

        <FormInput
          label="Confirm Password"
          name="confirm_password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Change Password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ==================== DEALER PORTAL USER FORM ====================
function DealerPortalUserForm({
  user,
  onSave,
  onCancel,
}: {
  user: Partial<DealerPortalUser> | null;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}) {
  const isEditing = !!user?.id;
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone_work: user?.phone_work || '',
    phone_mobile: user?.phone_mobile || '',
    phone_home: user?.phone_home || '',
    company: user?.company || '',
    address_street: user?.address_street || '',
    address_city: user?.address_city || '',
    address_state: user?.address_state || '',
    address_zip: user?.address_zip || '',
    address_country: user?.address_country || 'United States',
    discount_percent: user?.discount_percent || 0,
    dealer_type: user?.dealer_type || 'core_window_treatment',
    status: user?.status || 'active',
    password: '',
    confirm_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'discount_percent' ? Number(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditing) {
      if (form.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (form.password !== form.confirm_password) {
        setError('Passwords do not match');
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const data: any = { ...form };
      delete data.confirm_password;
      if (isEditing) delete data.password;
      if (!isEditing) delete data.status;
      await onSave(data);
    } catch (err: any) {
      setError(err.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <FormInput label="First Name" name="first_name" value={form.first_name} onChange={handleChange} required />
        <FormInput label="Last Name" name="last_name" value={form.last_name} onChange={handleChange} required />
      </div>

      <FormInput label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />

      {!isEditing && (
        <>
          <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-lg text-sm">
            <strong>Password Requirements:</strong> Minimum 8 characters
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Password" name="password" type="password" value={form.password} onChange={handleChange} required minLength={8} />
            <FormInput label="Confirm Password" name="confirm_password" type="password" value={form.confirm_password} onChange={handleChange} required />
          </div>
        </>
      )}

      <div className="grid grid-cols-3 gap-4">
        <FormInput label="Work Phone" name="phone_work" value={form.phone_work} onChange={handleChange} />
        <FormInput label="Mobile Phone" name="phone_mobile" value={form.phone_mobile} onChange={handleChange} />
        <FormInput label="Home Phone" name="phone_home" value={form.phone_home} onChange={handleChange} />
      </div>

      <FormInput label="Company" name="company" value={form.company} onChange={handleChange} />
      <FormInput label="Street Address" name="address_street" value={form.address_street} onChange={handleChange} />

      <div className="grid grid-cols-2 gap-4">
        <FormInput label="City" name="address_city" value={form.address_city} onChange={handleChange} />
        <FormSelect label="State" name="address_state" value={form.address_state} onChange={handleChange} options={US_STATES} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormInput label="ZIP Code" name="address_zip" value={form.address_zip} onChange={handleChange} />
        <FormSelect label="Country" name="address_country" value={form.address_country} onChange={handleChange} options={COUNTRIES} />
      </div>

      <div className={`grid ${isEditing ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
        <FormInput label="Dealer Discount %" name="discount_percent" type="number" value={form.discount_percent} onChange={handleChange} />
        <FormSelect label="Dealer Type" name="dealer_type" value={form.dealer_type} onChange={handleChange} options={DEALER_TYPES} />
        {isEditing && (
          <FormSelect label="Status" name="status" value={form.status} onChange={handleChange} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : isEditing ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
}

// ==================== EMPLOYEE FORM ====================
function EmployeeForm({
  employee,
  workstations,
  territories,
  employees,
  onSave,
  onCancel,
}: {
  employee: Partial<Employee> | null;
  workstations: Workstation[];
  territories: Territory[];
  employees: Employee[];
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}) {
  const isEditing = !!employee?.id;
  const [form, setForm] = useState({
    // Basic Info
    first_name: employee?.first_name || '',
    last_name: employee?.last_name || '',
    employee_id: employee?.employee_id || '',
    email: employee?.email || '',
    phone_work: employee?.phone_work || '',
    phone_mobile: employee?.phone_mobile || '',
    job_title: employee?.job_title || '',
    department: employee?.department || '',
    hire_date: employee?.hire_date?.split('T')[0] || '',
    hourly_rate: employee?.hourly_rate?.toString() || '',
    emergency_contact_name: employee?.emergency_contact_name || '',
    emergency_contact_phone: employee?.emergency_contact_phone || '',
    notes: employee?.notes || '',
    status: employee?.status || 'active',
    // Address (for tax document mailing)
    street: employee?.street || '',
    city: employee?.city || '',
    state: employee?.state || '',
    zip: employee?.zip || '',
    // Payroll Classification
    payroll_classification: employee?.payroll_classification || '',
    payroll_pay_type: employee?.payroll_pay_type || '',
    payroll_pay_rate: employee?.payroll_pay_rate?.toString() || '',
    payroll_filing_status: employee?.payroll_filing_status || '',
    holiday_pay_eligible: employee?.holiday_pay_eligible || false,
    withholding_method: employee?.withholding_method || 'standard',
    flat_withholding_amount: employee?.flat_withholding_amount?.toString() || '',
    flat_withholding_percent: employee?.flat_withholding_percent?.toString() || '',
    additional_withholding: employee?.additional_withholding?.toString() || '',
    // Master Access
    all_access: employee?.all_access || false,
    // Workstation Access
    workstation_access: employee?.workstation_access || false,
    workstation_pin: employee?.workstation_pin || '',
    assigned_workstation_ids: employee?.assigned_workstation_ids || [],
    // Factory Admin Access
    factory_admin_access: employee?.factory_admin_access || false,
    factory_admin_role: employee?.factory_admin_role || 'viewer',
    // Sales Portal Access
    sales_portal_access: employee?.sales_portal_access || false,
    sales_role: employee?.sales_role || 'sales_rep',
    sales_team: employee?.sales_team || 'core_window_treatment',
    territory_ids: employee?.territory_ids || [],
    selected_states: employee?.selected_states || [],
    reports_to_id: employee?.reports_to_id || '',
    commission_rate_override: employee?.commission_rate_override?.toString() || '',
    // Shipping Portal Access
    shipping_portal_access: employee?.shipping_portal_access || false,
    shipping_role: employee?.shipping_role || 'shipping_employee',
    // Maintenance Portal Access
    maintenance_access: employee?.maintenance_access || false,
    rep_portal_access: employee?.rep_portal_access || false,
    maintenance_role: employee?.maintenance_role || 'technician',
    // Health Dashboard Access
    health_dashboard_access: employee?.health_dashboard_access || false,
    // Accounting Portal Access
    accounting_access: employee?.accounting_access || false,
    // Manufacturing Rules Edit Permission
    can_edit_manufacturing_rules: employee?.can_edit_manufacturing_rules || false,
    // Inventory Permission
    can_add_inventory: employee?.can_add_inventory || false,
    // Training Mode
    training_mode_default: employee?.training_mode_default !== undefined ? employee.training_mode_default : true,
    lock_training_mode: employee?.lock_training_mode || false,
    work_cards_only: employee?.work_cards_only || false,
    lock_production_mode: employee?.lock_production_mode || false,
    // Discipline
    discipline_access: employee?.discipline_access || false,
    reviews_access: employee?.reviews_access || false,
    discipline_subject: employee?.discipline_subject ?? true,
    // Password
    password: '',
    confirm_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: ['hourly_rate', 'commission_rate_override', 'payroll_pay_rate'].includes(name) ? value : value,
    }));
  };

  const handleToggle = (name: string, value: boolean) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditing) {
      if (form.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (form.password !== form.confirm_password) {
        setError('Passwords do not match');
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const data: any = { ...form };
      delete data.confirm_password;
      if (isEditing) delete data.password;
      if (!isEditing) delete data.status;
      // Convert empty strings to null/undefined
      if (!data.employee_id) delete data.employee_id;
      if (!data.hourly_rate) delete data.hourly_rate;
      else data.hourly_rate = parseFloat(data.hourly_rate);
      if (!data.hire_date) delete data.hire_date;
      if (!data.commission_rate_override) delete data.commission_rate_override;
      else data.commission_rate_override = parseFloat(data.commission_rate_override);
      if (!data.reports_to_id) delete data.reports_to_id;
      if (!data.workstation_pin) delete data.workstation_pin;
      // Payroll fields
      if (!data.payroll_pay_rate) delete data.payroll_pay_rate;
      else data.payroll_pay_rate = parseFloat(data.payroll_pay_rate);
      if (!data.payroll_classification) delete data.payroll_classification;
      if (!data.payroll_pay_type) delete data.payroll_pay_type;
      if (!data.payroll_filing_status) delete data.payroll_filing_status;
      // Withholding numeric fields → numbers (empty = 0, so a cleared field zeroes).
      data.flat_withholding_amount = parseFloat(data.flat_withholding_amount) || 0;
      data.flat_withholding_percent = parseFloat(data.flat_withholding_percent) || 0;
      data.additional_withholding = parseFloat(data.additional_withholding) || 0;
      await onSave(data);
    } catch (err: any) {
      setError(err.message || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const allAccessEnabled = form.all_access;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {/* Basic Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="First Name" name="first_name" value={form.first_name} onChange={handleChange} required />
            <FormInput label="Last Name" name="last_name" value={form.last_name} onChange={handleChange} required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Employee ID" name="employee_id" value={form.employee_id} onChange={handleChange} placeholder="Auto-generated if blank" disabled={isEditing} />
            <FormInput label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
          </div>

          {!isEditing && (
            <>
              <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-lg text-sm">
                <strong>Password Requirements:</strong> Minimum 8 characters
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput label="Password" name="password" type="password" value={form.password} onChange={handleChange} required minLength={8} />
                <FormInput label="Confirm Password" name="confirm_password" type="password" value={form.confirm_password} onChange={handleChange} required />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Work Phone" name="phone_work" value={form.phone_work} onChange={handleChange} />
            <FormInput label="Mobile Phone" name="phone_mobile" value={form.phone_mobile} onChange={handleChange} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Job Title" name="job_title" value={form.job_title} onChange={handleChange} />
            <FormSelect label="Department" name="department" value={form.department} onChange={handleChange} options={DEPARTMENTS} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Hire Date" name="hire_date" type="date" value={form.hire_date} onChange={handleChange} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Rate</label>
              <a
                href="/hr/payroll"
                target="_blank"
                rel="noreferrer"
                className="block w-full px-3 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded text-sm hover:bg-blue-100 text-center"
              >
                Manage in HR → Payroll →
              </a>
              <p className="text-xs text-gray-500 mt-1">
                Pay rate / classification / W-2 vs 1099 lives in HR Payroll.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Emergency Contact Name" name="emergency_contact_name" value={form.emergency_contact_name} onChange={handleChange} />
            <FormInput label="Emergency Contact Phone" name="emergency_contact_phone" value={form.emergency_contact_phone} onChange={handleChange} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {isEditing && (
            <FormSelect label="Status" name="status" value={form.status} onChange={handleChange} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
          )}
        </div>
      </div>

      {/* Mailing Address */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Mailing Address</h3>
        <p className="text-sm text-gray-500 mb-4">For W-2 and 1099 tax document mailing at year end</p>
        <div className="space-y-4">
          <FormInput label="Street Address" name="street" value={form.street} onChange={handleChange} placeholder="123 Main Street" />
          <div className="grid grid-cols-3 gap-4">
            <FormInput label="City" name="city" value={form.city} onChange={handleChange} />
            <FormSelect label="State" name="state" value={form.state} onChange={handleChange} options={US_STATES.map(s => ({ value: s, label: s }))} placeholder="Select state" />
            <FormInput label="ZIP Code" name="zip" value={form.zip} onChange={handleChange} placeholder="12345" />
          </div>
        </div>
      </div>

      {/* Payroll Classification */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Payroll Classification</h3>
        <p className="text-sm text-gray-500 mb-4">Tax classification and pay settings for payroll processing</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormSelect
              label="Tax Classification"
              name="payroll_classification"
              value={form.payroll_classification}
              onChange={handleChange}
              options={PAYROLL_CLASSIFICATIONS}
              placeholder="Select classification"
            />
            <FormSelect
              label="Pay Type"
              name="payroll_pay_type"
              value={form.payroll_pay_type}
              onChange={handleChange}
              options={PAY_TYPES}
              placeholder="Select pay type"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              label={form.payroll_pay_type === 'salary' ? 'Annual Salary ($)' : 'Pay Rate ($)'}
              name="payroll_pay_rate"
              type="number"
              value={form.payroll_pay_rate}
              onChange={handleChange}
              placeholder={form.payroll_pay_type === 'salary' ? 'e.g. 50000' : 'e.g. 25.00'}
            />
            <FormSelect
              label="Tax Filing Status"
              name="payroll_filing_status"
              value={form.payroll_filing_status}
              onChange={handleChange}
              options={FILING_STATUSES}
              placeholder="Select filing status"
            />
          </div>
          <ToggleSwitch
            label="Paid company holidays"
            description="Earns holiday pay for company holidays on days not worked (uses the Company Holidays list on the calendar). Off = no holiday pay."
            checked={form.holiday_pay_eligible}
            onChange={(checked) => handleToggle('holiday_pay_eligible', checked)}
          />

          {/* Federal tax withholding method */}
          <div className="border-t pt-4 mt-2">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Federal Tax Withholding</h4>
            <p className="text-xs text-gray-500 mb-3">How federal income tax is withheld each paycheck. Social Security + Medicare still apply to W-2 employees regardless of method.</p>
            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                label="Withholding Method"
                name="withholding_method"
                value={form.withholding_method}
                onChange={handleChange}
                options={[
                  { value: 'standard', label: 'Standard (IRS equation)' },
                  { value: 'flat_amount', label: 'Flat $ amount' },
                  { value: 'flat_percent', label: 'Flat %' },
                  { value: 'none', label: 'None (defer to year-end)' },
                ]}
              />
              {form.withholding_method === 'flat_amount' && (
                <FormInput
                  label="Flat Amount ($/paycheck)"
                  name="flat_withholding_amount"
                  type="number"
                  value={form.flat_withholding_amount}
                  onChange={handleChange}
                  placeholder="e.g. 120.00"
                />
              )}
              {form.withholding_method === 'flat_percent' && (
                <FormInput
                  label="Flat Percent (% of gross)"
                  name="flat_withholding_percent"
                  type="number"
                  value={form.flat_withholding_percent}
                  onChange={handleChange}
                  placeholder="e.g. 18"
                />
              )}
            </div>
            {form.withholding_method !== 'none' && (
              <div className="mt-4 max-w-[50%]">
                <FormInput
                  label="Additional Withholding ($/paycheck)"
                  name="additional_withholding"
                  type="number"
                  value={form.additional_withholding}
                  onChange={handleChange}
                  placeholder="extra on top, e.g. 10.00"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {isEditing && employee?.id && (
        <TimeOffBalancesSection employeeId={employee.id} />
      )}

      {/* Permissions */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Portal Access & Permissions</h3>

        {/* ALL ACCESS Toggle */}
        <div className="mb-4">
          <ToggleSwitch
            label="ALL ACCESS (Super User)"
            description="Grants full access to all portals with admin privileges. Overrides all individual permissions."
            checked={form.all_access}
            onChange={(checked) => handleToggle('all_access', checked)}
            highlight
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Workstation Access */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Workstation Access"
              description="Can clock in/out and complete tasks at workstations"
              checked={form.workstation_access || allAccessEnabled}
              onChange={(checked) => handleToggle('workstation_access', checked)}
              disabled={allAccessEnabled}
            />
            {(form.workstation_access || allAccessEnabled) && (
              <div className="mt-4 space-y-3 pl-2 border-l-2 border-blue-200">
                <FormInput
                  label="PIN (4 digits)"
                  name="workstation_pin"
                  value={form.workstation_pin}
                  onChange={handleChange}
                  placeholder="4-digit PIN for workstation login"
                />
                <MultiSelect
                  label="Assigned Workstations"
                  options={workstations.map((ws) => ({ id: ws.id, name: ws.name, group_name: ws.group_name }))}
                  selected={form.assigned_workstation_ids}
                  onChange={(selected) => setForm((prev) => ({ ...prev, assigned_workstation_ids: selected }))}
                />
                <ToggleSwitch
                  label="Training Mode Default"
                  description="When ON, employee sees full SOP step-by-step instructions. When OFF, employee sees simplified production mode."
                  checked={form.training_mode_default}
                  onChange={(checked) => handleToggle('training_mode_default', checked)}
                />
                <ToggleSwitch
                  label="Keep in Training Mode"
                  description="When ON, employee cannot switch out of training mode on the workstation. They will always see full SOP instructions."
                  checked={form.lock_training_mode}
                  onChange={(checked) => {
                    handleToggle('lock_training_mode', checked);
                    // Can't be locked into both — turning one on releases the other.
                    if (checked) handleToggle('lock_production_mode', false);
                  }}
                />
                <ToggleSwitch
                  label="Keep in Production Mode"
                  description="When ON, employee cannot switch out of production mode — no training or expert view. Use for operators who should work the simplified step list only."
                  checked={form.lock_production_mode}
                  onChange={(checked) => {
                    handleToggle('lock_production_mode', checked);
                    if (checked) handleToggle('lock_training_mode', false);
                  }}
                />
                <ToggleSwitch
                  label="Work Cards Only"
                  description="When ON, this employee only sees work cards. The order detail page, cut sheet and BOM are hidden for them — tapping an order shows the station list, and tapping a station opens the work card. Everyone else is unaffected."
                  checked={form.work_cards_only}
                  onChange={(checked) => handleToggle('work_cards_only', checked)}
                />
              </div>
            )}
          </div>

          {/* Factory Admin Access */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Factory Admin Access"
              description="Access to the Factory Admin portal"
              checked={form.factory_admin_access || allAccessEnabled}
              onChange={(checked) => handleToggle('factory_admin_access', checked)}
              disabled={allAccessEnabled}
            />
            {(form.factory_admin_access || allAccessEnabled) && (
              <div className="mt-4 pl-2 border-l-2 border-blue-200">
                <FormSelect
                  label="Role"
                  name="factory_admin_role"
                  value={allAccessEnabled ? 'admin' : form.factory_admin_role}
                  onChange={handleChange}
                  options={FACTORY_ADMIN_ROLES}
                  disabled={allAccessEnabled}
                />
              </div>
            )}
          </div>

          {/* Discipline */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <ToggleSwitch
              label="Discipline Access"
              description="Can open HR → Discipline: see every employee's infractions and warnings, issue them, and open their attachments. All Access also grants this."
              checked={form.discipline_access || allAccessEnabled}
              onChange={(checked) => handleToggle('discipline_access', checked)}
              disabled={allAccessEnabled}
            />
            <ToggleSwitch
              label="Subject to Discipline"
              description="When ON, this employee appears on the Discipline page and infractions or warnings can be recorded against them. Turn OFF for owners and managers who aren't on the discipline ladder. They can still be picked as a witness or as the person who delivered a warning."
              checked={form.discipline_subject}
              onChange={(checked) => handleToggle('discipline_subject', checked)}
            />
          </div>

          {/* Reviews access */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Reviews Access"
              description="Can open HR → Reviews: schedule and run employee development reviews. Reviews are not discipline and never create a disciplinary record. All Access also grants this."
              checked={form.reviews_access || allAccessEnabled}
              onChange={(checked) => handleToggle('reviews_access', checked)}
              disabled={allAccessEnabled}
            />
          </div>

          {/* Sales Portal Access */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Sales Portal Access"
              description="Access to the Sales Portal for managing dealers"
              checked={form.sales_portal_access || allAccessEnabled}
              onChange={(checked) => handleToggle('sales_portal_access', checked)}
              disabled={allAccessEnabled}
            />
            {(form.sales_portal_access || allAccessEnabled) && (
              <div className="mt-4 space-y-3 pl-2 border-l-2 border-blue-200">
                <div className="grid grid-cols-2 gap-3">
                  <FormSelect
                    label="Sales Role"
                    name="sales_role"
                    value={allAccessEnabled ? 'vp_of_sales' : form.sales_role}
                    onChange={handleChange}
                    options={SALES_ROLES}
                    disabled={allAccessEnabled}
                  />
                  <FormSelect
                    label="Team"
                    name="sales_team"
                    value={form.sales_team}
                    onChange={handleChange}
                    options={SALES_TEAMS}
                  />
                </div>
                <TerritoryStateSelect
                  territories={territories}
                  selectedTerritories={form.territory_ids}
                  selectedStates={form.selected_states}
                  onTerritoriesChange={(selected) => setForm((prev) => ({ ...prev, territory_ids: selected }))}
                  onStatesChange={(selected) => setForm((prev) => ({ ...prev, selected_states: selected }))}
                />
                <FormSelect
                  label="Reports To"
                  name="reports_to_id"
                  value={form.reports_to_id}
                  onChange={handleChange}
                  options={employees
                    .filter((e) => e.id !== employee?.id && e.sales_portal_access && e.sales_role)
                    .map((e) => ({
                      value: e.id,
                      label: `${e.first_name} ${e.last_name} (${SALES_ROLES.find((r) => r.value === e.sales_role)?.label || e.sales_role})`,
                    }))}
                  placeholder="Select manager (optional)"
                />
                <FormInput
                  label="Commission Rate Override %"
                  name="commission_rate_override"
                  type="number"
                  value={form.commission_rate_override}
                  onChange={handleChange}
                  placeholder="Leave blank for default"
                  hint="Default: Rep 4%, TM 3%, RD 2%, VP 1%"
                />
              </div>
            )}
          </div>

          {/* Shipping Portal Access */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Shipping Portal Access"
              description="Access to the Shipping Portal for managing shipments"
              checked={form.shipping_portal_access || allAccessEnabled}
              onChange={(checked) => handleToggle('shipping_portal_access', checked)}
              disabled={allAccessEnabled}
            />
            {(form.shipping_portal_access || allAccessEnabled) && (
              <div className="mt-4 pl-2 border-l-2 border-blue-200">
                <FormSelect
                  label="Shipping Role"
                  name="shipping_role"
                  value={allAccessEnabled ? 'shipping_admin' : form.shipping_role}
                  onChange={handleChange}
                  options={SHIPPING_ROLES}
                  disabled={allAccessEnabled}
                />
              </div>
            )}
          </div>

          {/* Maintenance Portal Access */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Maintenance Portal Access"
              description="Access to equipment maintenance, PM schedules, and work orders"
              checked={form.maintenance_access || allAccessEnabled}
              onChange={(checked) => handleToggle('maintenance_access', checked)}
              disabled={allAccessEnabled}
            />
            {(form.maintenance_access || allAccessEnabled) && (
              <div className="mt-4 pl-2 border-l-2 border-blue-200">
                <FormSelect
                  label="Maintenance Role"
                  name="maintenance_role"
                  value={allAccessEnabled ? 'admin' : form.maintenance_role}
                  onChange={handleChange}
                  options={MAINTENANCE_ROLES}
                  disabled={allAccessEnabled}
                />
              </div>
            )}
          </div>

          {/* Rep Firm Portal Access */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Rep Firm Portal Access"
              description="Access to the Rep Firm Portal for managing rep firm dealers, orders, and commissions"
              checked={form.rep_portal_access || allAccessEnabled}
              onChange={(checked) => handleToggle('rep_portal_access', checked)}
              disabled={allAccessEnabled}
            />
            {(form.rep_portal_access || allAccessEnabled) && (
              <div className="mt-4 pl-2 border-l-2 border-purple-200">
                <p className="text-sm text-gray-600">
                  View all rep firm dealers, orders, commissions, and support tickets. Admin view shows data across all rep firms.
                </p>
              </div>
            )}
          </div>

          {/* Accounting Section Access (within Factory Admin) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Accounting Section Access"
              description="Access to the Accounting section in Factory Admin (bills, payroll, dealer approvals, revenue reports)"
              checked={form.accounting_access || allAccessEnabled}
              onChange={(checked) => handleToggle('accounting_access', checked)}
              disabled={allAccessEnabled}
            />
            {(form.accounting_access || allAccessEnabled) && (
              <div className="mt-4 pl-2 border-l-2 border-blue-200">
                <p className="text-xs text-gray-500 mb-2">
                  Shows the Accounting dropdown in Factory Admin sidebar. If this is the only access granted (no Factory Admin role), the user will only see accounting pages.
                </p>
              </div>
            )}
          </div>

          {/* Health Dashboard Access */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Health Dashboard Access"
              description="Access to system health monitoring"
              checked={form.health_dashboard_access || allAccessEnabled}
              onChange={(checked) => handleToggle('health_dashboard_access', checked)}
              disabled={allAccessEnabled}
            />
            {(form.health_dashboard_access || allAccessEnabled) && (
              <div className="mt-4 pl-2 border-l-2 border-blue-200">
                <p className="text-sm text-gray-600">
                  View server stats, service health checks, and system logs
                </p>
              </div>
            )}
          </div>

          {/* Manufacturing Rules Edit Permission */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Manufacturing Rules Edit"
              description="Permission to edit manufacturing rules and formulas"
              checked={form.can_edit_manufacturing_rules || allAccessEnabled}
              onChange={(checked) => handleToggle('can_edit_manufacturing_rules', checked)}
              disabled={allAccessEnabled}
            />
            {(form.can_edit_manufacturing_rules || allAccessEnabled) && (
              <div className="mt-4 pl-2 border-l-2 border-amber-200">
                <p className="text-sm text-gray-600">
                  Edit cut formulas, installation adjustments, seam rules, validation rules, and spring configuration.
                  <span className="block text-amber-600 mt-1 font-medium">
                    ⚠️ Changes require multi-step confirmation and are logged.
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Inventory Management Permission */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ToggleSwitch
              label="Inventory Management"
              description="Can view, adjust, and receive inventory from workstation"
              checked={form.can_add_inventory || allAccessEnabled}
              onChange={(checked) => handleToggle('can_add_inventory', checked)}
              disabled={allAccessEnabled}
            />
            {(form.can_add_inventory || allAccessEnabled) && (
              <div className="mt-4 pl-2 border-l-2 border-green-200">
                <p className="text-sm text-gray-600">
                  Access to the Inventory tab in the Workstation PWA. Can adjust stock counts and receive new inventory.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : isEditing ? 'Update Employee' : 'Create Employee'}
        </button>
      </div>
    </form>
  );
}

// ==================== MAIN PAGE ====================
// ==================== REP FIRM CONTACTS SECTION ====================
function RepFirmContactsSection() {
  const [firms, setFirms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFirm, setExpandedFirm] = useState<string | null>(null);
  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState<string | null>(null);
  const [newContact, setNewContact] = useState({ email: '', firstName: '', lastName: '', phone: '', role: 'rep' });
  const [saving, setSaving] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  useEffect(() => { loadFirms(); }, []);

  const loadFirms = async () => {
    try { setFirms(await apiRequest('/rep-firms')); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadContacts = async (firmId: string) => {
    setContactsLoading(true);
    try { setContacts(await apiRequest(`/rep-firms/${firmId}/contacts`)); } catch (e) { console.error(e); }
    finally { setContactsLoading(false); }
  };

  const toggleFirm = (firmId: string) => {
    if (expandedFirm === firmId) { setExpandedFirm(null); setContacts([]); }
    else { setExpandedFirm(firmId); loadContacts(firmId); }
  };

  const handleCreate = async () => {
    if (!showAddModal || !newContact.email) return;
    setSaving(true);
    try {
      const result = await apiRequest(`/rep-firms/${showAddModal}/contacts`, {
        method: 'POST', body: JSON.stringify(newContact),
      });
      setGeneratedPassword(result.password);
      loadContacts(showAddModal);
    } catch (err: any) { alert(err.message || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const handleReset = async (firmId: string, contactId: string, email: string) => {
    if (!confirm(`Reset password for ${email}?`)) return;
    try {
      const r = await apiRequest(`/rep-firms/${firmId}/contacts/${contactId}/reset-password`, { method: 'POST' });
      alert(`New password for ${email}:\n\n${r.password}\n\nSave this — it will not be shown again.`);
    } catch (err: any) { alert(err.message); }
  };

  const handleDeactivate = async (firmId: string, contactId: string, email: string) => {
    if (!confirm(`Deactivate ${email}?\n\nThey can be reactivated later. To remove permanently, use Delete.`)) return;
    try { await apiRequest(`/rep-firms/${firmId}/contacts/${contactId}`, { method: 'DELETE' }); loadContacts(firmId); }
    catch (err: any) { alert(err.message); }
  };

  const handleHardDelete = async (firmId: string, contactId: string, email: string) => {
    if (!confirm(`PERMANENTLY DELETE ${email}?\n\nThis removes the user completely. You'll get an error if they have dealers or leads still assigned — reassign those first.\n\nThis cannot be undone.`)) return;
    try {
      await apiRequest(`/rep-firms/${firmId}/contacts/${contactId}?hard=true`, { method: 'DELETE' });
      loadContacts(firmId);
    } catch (err: any) { alert(err.message || 'Failed to delete'); }
  };

  const handleReactivate = async (firmId: string, contactId: string) => {
    try {
      await apiRequest(`/rep-firms/${firmId}/contacts/${contactId}`, {
        method: 'PATCH', body: JSON.stringify({ status: 'active' }),
      });
      loadContacts(firmId);
    } catch (err: any) { alert(err.message); }
  };

  const [editingContact, setEditingContact] = useState<any>(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', phone: '', role: '' });

  const startEditContact = (c: any) => {
    setEditingContact(c);
    setEditForm({
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phone: c.phone || '',
      role: c.role || 'rep',
    });
  };

  const saveEditContact = async () => {
    if (!editingContact) return;
    setSaving(true);
    try {
      await apiRequest(`/rep-firms/${editingContact.repFirmId || editingContact.firmId || expandedFirm}/contacts/${editingContact.id}`, {
        method: 'PATCH', body: JSON.stringify(editForm),
      });
      const firmId = editingContact.repFirmId || editingContact.firmId || expandedFirm;
      setEditingContact(null);
      if (firmId) loadContacts(firmId);
    } catch (err: any) { alert(err.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleLoginAs = async (contactId: string, name: string) => {
    try {
      const result = await apiRequest(`/auth/impersonate-rep-contact/${contactId}`, { method: 'POST' });
      window.open(`https://reps.kederscreens.com/?impersonate_token=${result.token}`, '_blank');
    } catch (err: any) { alert(err.message || 'Failed to impersonate'); }
  };

  const closeModal = () => { setShowAddModal(null); setNewContact({ email: '', firstName: '', lastName: '', phone: '', role: 'rep' }); setGeneratedPassword(null); };

  if (loading) return <div className="flex justify-center py-8"><LoadingSpinner /></div>;

  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Rep Firm Portal Logins</h3>
        <p className="text-sm text-gray-500">Manage login accounts for reps.kederscreens.com</p>
      </div>
      {firms.length === 0 ? <p className="text-gray-500 py-4 text-center">No rep firms found</p> : (
        <div className="space-y-2">
          {firms.map((firm: any) => (
            <div key={firm.id} className="border rounded-lg overflow-hidden">
              <button onClick={() => toggleFirm(firm.id)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left">
                <div className="flex items-center gap-3">
                  <span className="text-purple-600 font-semibold">{firm.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${firm.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{firm.status}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={(e) => { e.stopPropagation(); setShowAddModal(firm.id); }} className="px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700">+ Add Login</button>
                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${expandedFirm === firm.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </button>
              {expandedFirm === firm.id && (
                <div className="border-t">
                  {contactsLoading ? <div className="p-4 text-center text-gray-400">Loading...</div> : contacts.length === 0 ? (
                    <div className="p-4 text-center text-gray-400">No portal logins. Click "+ Add Login" to create one.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs text-gray-500 uppercase bg-gray-50/50">
                        <th className="px-4 py-2">Name</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Last Login</th><th className="px-4 py-2 text-right">Actions</th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {contacts.map((c: any) => (
                          <tr key={c.id} className={c.status !== 'active' ? 'opacity-50' : ''}>
                            <td className="px-4 py-2.5 font-medium">{c.firstName} {c.lastName}{c.isPrimary && <span className="ml-1 text-xs text-green-600">(Primary)</span>}</td>
                            <td className="px-4 py-2.5 text-gray-600">{c.email}</td>
                            <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${c.role === 'owner' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{c.role}</span></td>
                            <td className="px-4 py-2.5"><span className={c.status === 'active' ? 'text-green-600' : 'text-gray-400'}>{c.status}</span></td>
                            <td className="px-4 py-2.5 text-gray-400 text-xs">{c.lastLoginAt ? new Date(c.lastLoginAt).toLocaleDateString() : 'Never'}</td>
                            <td className="px-4 py-2.5 text-right space-x-2">
                              {c.status === 'active' && <button onClick={() => handleLoginAs(c.id, `${c.firstName} ${c.lastName}`)} className="text-xs text-purple-600 hover:text-purple-800 font-medium">Login As</button>}
                              <button onClick={() => startEditContact({ ...c, firmId: firm.id })} className="text-xs text-gray-700 hover:text-gray-900">Edit</button>
                              <button onClick={() => handleReset(firm.id, c.id, c.email)} className="text-xs text-blue-600 hover:text-blue-800">Reset PW</button>
                              {c.status === 'active'
                                ? <button onClick={() => handleDeactivate(firm.id, c.id, c.email)} className="text-xs text-amber-600 hover:text-amber-800">Deactivate</button>
                                : <button onClick={() => handleReactivate(firm.id, c.id)} className="text-xs text-green-600 hover:text-green-800">Reactivate</button>
                              }
                              <button onClick={() => handleHardDelete(firm.id, c.id, c.email)} className="text-xs text-red-600 hover:text-red-800">Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            {generatedPassword ? (
              <>
                <h3 className="text-lg font-semibold text-green-700 mb-4">Login Created!</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-1">Portal:</p>
                  <p className="font-mono text-sm font-bold mb-3">https://reps.kederscreens.com</p>
                  <p className="text-sm text-gray-600 mb-1">Email:</p>
                  <p className="font-mono text-sm font-bold mb-3">{newContact.email}</p>
                  <p className="text-sm text-gray-600 mb-1">Password:</p>
                  <p className="font-mono text-lg font-bold text-green-800 select-all">{generatedPassword}</p>
                </div>
                <p className="text-xs text-red-600 mb-4">Save this password now — it will not be shown again.</p>
                <button onClick={closeModal} className="w-full py-2 bg-gray-800 text-white rounded-lg">Done</button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4">Add Rep Firm Login</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" placeholder="rep@company.com" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">First Name</label><input type="text" value={newContact.firstName} onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" /></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label><input type="text" value={newContact.lastName} onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" /></div>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="tel" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" /></div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select value={newContact.role} onChange={(e) => setNewContact({ ...newContact, role: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none">
                      <option value="owner">Owner (sees all firm data)</option>
                      <option value="admin">Admin</option>
                      <option value="rep">Rep</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={closeModal} className="flex-1 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button onClick={handleCreate} disabled={saving || !newContact.email} className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">{saving ? 'Creating...' : 'Create Login'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Edit Contact Modal */}
      {editingContact && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Edit Rep Firm Login</h3>
            <div className="space-y-3">
              <div className="text-xs text-gray-500 bg-gray-50 rounded px-3 py-2">
                Email <span className="font-mono text-gray-700">{editingContact.email}</span> can't be changed (it's the login identity).
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input type="text" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input type="text" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none">
                  <option value="owner">Owner (sees all firm data)</option>
                  <option value="admin">Admin</option>
                  <option value="rep">Rep</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditingContact(null)} className="flex-1 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={saveEditContact} disabled={saving} className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export function UserManagementPage() {
  const [activeTab, setActiveTab] = useState<TabType>('dealer-portal');
  const [dealerUsers, setDealerUsers] = useState<DealerPortalUser[]>([]);
  const [dealerTotal, setDealerTotal] = useState(0);
  const [dealerPage, setDealerPage] = useState(1);
  const [dealerPages, setDealerPages] = useState(1);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<{ id: string; name: string } | null>(null);
  const [resendingCredentials, setResendingCredentials] = useState<string | null>(null);
  const DEALER_PAGE_SIZE = 50;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setDealerPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load dealer data with pagination
  const loadDealerData = async (page: number, search: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(DEALER_PAGE_SIZE), page: String(page) });
      if (search) params.set('search', search);
      const dealers = await apiRequest(`/users/dealer-portal?${params}`);
      setDealerUsers(dealers.users || []);
      setDealerTotal(dealers.pagination?.total || 0);
      setDealerPages(dealers.pagination?.pages || 1);
    } catch (err) {
      console.error('Failed to load dealers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load non-dealer data (employees, workstations, territories)
  const loadSupportData = async () => {
    try {
      const [emps, ws, terr] = await Promise.all([
        apiRequest('/employees'),
        apiRequest('/users/workstations-list').catch(() => []),
        apiRequest('/territories').catch(() => []),
      ]);
      setEmployees(emps.employees || emps || []);
      setWorkstations(ws || []);
      setTerritories(terr || []);
    } catch (err) {
      console.error('Failed to load support data:', err);
    }
  };

  // Load support data once
  useEffect(() => {
    loadSupportData();
  }, []);

  // Load dealers when page or search changes
  useEffect(() => {
    loadDealerData(dealerPage, debouncedSearch);
  }, [dealerPage, debouncedSearch]);

  const handleCreateUser = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleChangePassword = (user: any) => {
    setSelectedUserForPassword({
      id: user.id,
      name: `${user.first_name} ${user.last_name}`,
    });
    setPasswordModalOpen(true);
  };

  const handleSaveUser = async (data: any) => {
    if (activeTab === 'dealer-portal') {
      const endpoint = '/users/dealer-portal';
      if (editingUser?.id) {
        await apiRequest(`${endpoint}/${editingUser.id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
      } else {
        await apiRequest(endpoint, {
          method: 'POST',
          body: JSON.stringify(data),
        });
      }
    } else {
      // Employees
      if (editingUser?.id) {
        await apiRequest(`/employees/${editingUser.id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
      } else {
        await apiRequest('/employees', {
          method: 'POST',
          body: JSON.stringify(data),
        });
      }
    }
    setModalOpen(false);
    loadDealerData(dealerPage, debouncedSearch);
    loadSupportData();
  };

  const handleDeleteUser = async (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      const endpoint = activeTab === 'dealer-portal'
        ? `/users/dealer-portal/${id}`
        : `/employees/${id}`;
      await apiRequest(endpoint, { method: 'DELETE' });
      loadDealerData(dealerPage, debouncedSearch);
    loadSupportData();
    } catch (err: any) {
      alert(err.message || 'Failed to deactivate user');
    }
  };

  const handleResendCredentials = async (user: any) => {
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
    if (!window.confirm(`Send new login credentials to ${name} (${user.email})? This will generate a new password and email it to them.`)) return;
    setResendingCredentials(user.id);
    try {
      await apiRequest(`/users/dealer-portal/${user.id}/send-credentials`, { method: 'POST' });
      alert(`New credentials sent to ${user.email}`);
    } catch (err: any) {
      alert(err.message || 'Failed to send credentials');
    } finally {
      setResendingCredentials(null);
    }
  };

  // Filter employees client-side (small list), dealers are server-side
  const filterEmployees = (users: any[]) => {
    if (!searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter((u) =>
      `${u.first_name} ${u.last_name} ${u.email} ${u.company || ''} ${u.employee_id || ''}`.toLowerCase().includes(term)
    );
  };

  const currentUsers = activeTab === 'dealer-portal'
    ? dealerUsers
    : filterEmployees(employees);

  const getAccessBadges = (emp: Employee) => {
    if (emp.all_access) {
      return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">ALL ACCESS</span>;
    }
    const badges = [];
    if (emp.workstation_access) badges.push(<span key="ws" className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">WS</span>);
    if (emp.factory_admin_access) badges.push(<span key="fa" className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">FA</span>);
    if (emp.sales_portal_access) badges.push(<span key="sp" className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Sales</span>);
    if (emp.shipping_portal_access) badges.push(<span key="sh" className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">Ship</span>);
    if (emp.maintenance_access) badges.push(<span key="mt" className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Maint</span>);
    if (emp.rep_portal_access) badges.push(<span key="rp" className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Rep</span>);
    if (emp.accounting_access) badges.push(<span key="ac" className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">Acct</span>);
    if (emp.health_dashboard_access) badges.push(<span key="hd" className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Health</span>);
    if (emp.can_edit_manufacturing_rules) badges.push(<span key="mfg" className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Mfg Rules</span>);
    if (emp.can_add_inventory) badges.push(<span key="inv" className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Inventory</span>);
    if (emp.discipline_access) badges.push(<span key="disc" className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Discipline</span>);
    if (emp.discipline_subject === false) badges.push(<span key="nodisc" className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Not on Discipline</span>);
    if (emp.work_cards_only) badges.push(<span key="wco" className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Cards Only</span>);
    if (emp.lock_production_mode) badges.push(<span key="lpm" className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Prod Locked</span>);
    if (emp.lock_training_mode) badges.push(<span key="ltm" className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Training Locked</span>);
    return badges.length > 0 ? <div className="flex gap-1 flex-wrap">{badges}</div> : <span className="text-gray-400">None</span>;
  };

  return (
    <>
      <PageHeader
        title="User Management"
        subtitle="Manage dealer users and employees"
        actions={
          <button onClick={handleCreateUser} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {activeTab === 'dealer-portal' ? 'Add Dealer User' : 'Add Employee'}
          </button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <TabButton active={activeTab === 'dealer-portal'} onClick={() => setActiveTab('dealer-portal')}>
          Dealer Portal Users ({dealerTotal})
        </TabButton>
        <TabButton active={activeTab === 'employees'} onClick={() => setActiveTab('employees')}>
          Employees ({employees.length})
        </TabButton>
        <TabButton active={activeTab === 'rep-firm'} onClick={() => setActiveTab('rep-firm')}>
          Rep Firm Logins
        </TabButton>
      </div>

      {activeTab === 'rep-firm' ? (
        <RepFirmContactsSection />
      ) : (
      <>
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Users Table */}
      <Card>
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-500 text-sm border-b">
                  <th className="pb-3 font-medium">Name</th>
                  <th className="pb-3 font-medium">Email</th>
                  {activeTab === 'dealer-portal' && <th className="pb-3 font-medium">Company</th>}
                  {activeTab === 'employees' && <th className="pb-3 font-medium">Employee ID</th>}
                  {activeTab === 'employees' && <th className="pb-3 font-medium">Department</th>}
                  {activeTab === 'dealer-portal' && <th className="pb-3 font-medium">Discount</th>}
                  {activeTab === 'employees' && <th className="pb-3 font-medium">Portal Access</th>}
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user: any) => (
                    <tr key={user.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 font-medium">{user.first_name} {user.last_name}</td>
                      <td className="py-3 text-gray-600">{user.email}</td>
                      {activeTab === 'dealer-portal' && <td className="py-3">{user.company || '-'}</td>}
                      {activeTab === 'employees' && <td className="py-3">{user.employee_id || '-'}</td>}
                      {activeTab === 'employees' && <td className="py-3">{user.department || '-'}</td>}
                      {activeTab === 'dealer-portal' && <td className="py-3">{user.discount_percent || 0}%</td>}
                      {activeTab === 'employees' && <td className="py-3">{getAccessBadges(user)}</td>}
                      <td className="py-3">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleEditUser(user)} className="text-blue-600 hover:text-blue-800">Edit</button>
                          <button onClick={() => handleChangePassword(user)} className="text-green-600 hover:text-green-800">Password</button>
                          {activeTab === 'dealer-portal' && (
                            <button
                              onClick={() => handleResendCredentials(user)}
                              disabled={resendingCredentials === user.id}
                              className="text-amber-600 hover:text-amber-800 disabled:opacity-50"
                            >
                              {resendingCredentials === user.id ? 'Sending...' : 'Send Login'}
                            </button>
                          )}
                          <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-800">Deactivate</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination for dealers */}
        {activeTab === 'dealer-portal' && dealerPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-gray-500">
              Showing {(dealerPage - 1) * DEALER_PAGE_SIZE + 1}–{Math.min(dealerPage * DEALER_PAGE_SIZE, dealerTotal)} of {dealerTotal}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setDealerPage(p => Math.max(1, p - 1))}
                disabled={dealerPage <= 1}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              {Array.from({ length: dealerPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === dealerPages || Math.abs(p - dealerPage) <= 2)
                .reduce<(number | string)[]>((acc, p, i, arr) => {
                  if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  typeof p === 'string' ? (
                    <span key={`gap-${i}`} className="px-2 py-1.5 text-gray-400">...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setDealerPage(p)}
                      className={`px-3 py-1.5 text-sm border rounded-lg ${p === dealerPage ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-gray-50'}`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setDealerPage(p => Math.min(dealerPages, p + 1))}
                disabled={dealerPage >= dealerPages}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* User Form Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`${editingUser ? 'Edit' : 'Create'} ${activeTab === 'dealer-portal' ? 'Dealer User' : 'Employee'}`}
        wide={activeTab === 'employees'}
      >
        {activeTab === 'dealer-portal' ? (
          <DealerPortalUserForm
            user={editingUser}
            onSave={handleSaveUser}
            onCancel={() => setModalOpen(false)}
          />
        ) : (
          <EmployeeForm
            employee={editingUser}
            workstations={workstations}
            territories={territories}
            employees={employees}
            onSave={handleSaveUser}
            onCancel={() => setModalOpen(false)}
          />
        )}
      </Modal>

      {/* Change Password Modal */}
      {selectedUserForPassword && (
        <ChangePasswordModal
          isOpen={passwordModalOpen}
          onClose={() => {
            setPasswordModalOpen(false);
            setSelectedUserForPassword(null);
          }}
          userType={activeTab}
          userId={selectedUserForPassword.id}
          userName={selectedUserForPassword.name}
        />
      )}
      </>
      )}
    </>
  );
}

export default UserManagementPage;
